import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface ProfileCriteria {
  icon: string;
  title: string;
  description: string;
}

interface ParticipantProfileProps {
  criteria: ProfileCriteria[];
}

const ParticipantProfile: React.FC<ParticipantProfileProps> = ({ criteria }) => {
  return (
    <section className="py-12 sm:py-16 lg:py-24 bg-surface">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary mb-4 sm:mb-6">
              Ideal Institutional Candidates
            </h2>
            <p className="text-base sm:text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed px-2">
              This program is designed for institutions committed to substantial transformation and prepared for sustained engagement over 6-12 months.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12">
            {criteria.map((criterion, index) => (
              <div
                key={index}
                className="bg-background rounded-lg institutional-border p-5 sm:p-8 hover:institutional-shadow transition-base"
              >
                <div className="flex items-start gap-4 sm:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-institutional/10 rounded-md flex items-center justify-center">
                      <Icon name={criterion.icon as any} size={20} className="text-institutional sm:hidden" />
                      <Icon name={criterion.icon as any} size={24} className="text-institutional hidden sm:block" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-xl font-semibold text-text-primary mb-2 sm:mb-3">
                      {criterion.title}
                    </h3>
                    <p className="text-xs sm:text-base text-text-secondary leading-relaxed">
                      {criterion.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-institutional/5 rounded-lg institutional-border p-5 sm:p-8 lg:p-12">
            <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-4 sm:mb-6">
              Commitment Requirements
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {[
                'Senior leadership participation in strategic sessions',
                'Dedicated internal coordination resources',
                'Access to organizational data and stakeholders',
                'Willingness to address institutional constraints',
                'Budget allocation for sustained engagement',
                'Commitment to implementation beyond planning',
              ].map((req, i) => (
                <div key={i} className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-institutional rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                  <span className="text-xs sm:text-sm text-text-secondary">{req}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ParticipantProfile;